import { AlertTriangle } from 'lucide-react'
import { Badge } from '../../../components/ui/badge'
import { Checkbox } from '../../../components/ui/checkbox'
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group'
import { cn } from '../../../lib/utils'
import { defaultSub, SOURCE_GROUPS, visibleSourceGroups } from './constants'
import { useHiddenIngestSources } from './useHiddenIngestSources'
import { WizardChrome } from './WizardChrome'
import type { SourceId, SubOptionId } from './types'
import type { IngestWizardApi } from './useIngestWizard'

interface StepSourcesProps {
  wizard: IngestWizardApi
}

export function StepSources({ wizard }: StepSourcesProps) {
  const { state, siteName, toggleSource, setSub, checkPlan, backTo1 } = wizard
  const count = state.selected.size
  const hidden = useHiddenIngestSources()

  return (
    <WizardChrome
      eyebrow="Step 02 — Data sources"
      title={`What data do you have for ${siteName || 'this site'}?`}
      description="Select everything you can provide. Files, exports, and connection details are collected in the next step — this only sets the scope."
      onBack={backTo1}
      onContinue={checkPlan}
      continueDisabled={count === 0}
    >
      <div className="flex flex-col gap-5">
        {visibleSourceGroups(SOURCE_GROUPS, hidden).map((group) => (
          <div key={group.title}>
            <div className="pb-2 text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">
              {group.title}
            </div>
            <div className="flex flex-col gap-1.5">
              {group.items.map((item) => {
                const checked = state.selected.has(item.id)
                const needsRecommendation =
                  (item.id === 'plc' && checked && !state.selected.has('ipmap')) ||
                  (item.id === 'robotics' && checked && !state.selected.has('roboticsmap')) ||
                  (item.id === 'vision' && checked && !state.selected.has('visionmap')) ||
                  (item.id === 'safetymanager' &&
                    checked &&
                    state.selected.has('srs') &&
                    !state.selected.has('safetymap'))
                return (
                  <div
                    key={item.id}
                    className={cn(
                      'overflow-hidden rounded-lg border motion-reduce:transition-none',
                      'transition-[border-color,background-color] duration-200 ease-out',
                      checked ? 'border-brand/40 bg-brand-soft/20' : 'border-line bg-bg-elev'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSource(item.id)}
                      className="flex w-full gap-2.5 border-0 bg-transparent px-3.5 py-3 text-left"
                    >
                      <Checkbox
                        checked={checked}
                        className="mt-0.5 pointer-events-none"
                        tabIndex={-1}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-14 font-medium text-ink">{item.label}</span>
                        <span className="mt-1 block text-xs leading-snug text-ink-3">
                          {item.desc}
                        </span>
                      </span>
                      {needsRecommendation ? (
                        <Badge
                          variant="secondary"
                          className="shrink-0 self-start border-transparent bg-st-warn-bg text-st-warn"
                        >
                          <AlertTriangle className="mr-1 size-3" />
                          Recommended
                        </Badge>
                      ) : null}
                    </button>

                    {checked && item.subOptions ? (
                      <div className="flex flex-wrap items-center gap-2.5 px-3.5 pb-3 pl-10.25">
                        <span className="text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">
                          {item.subLabel}
                        </span>
                        <ToggleGroup
                          type="single"
                          size="sm"
                          variant="outline"
                          value={state.sub[item.id] ?? defaultSub(item.id) ?? ''}
                          onValueChange={(v) => {
                            if (v) setSub(item.id, v as SubOptionId)
                          }}
                        >
                          {item.subOptions.map((o) => (
                            <ToggleGroupItem key={o.id} value={o.id} className="h-7 px-2.5 text-xs">
                              {o.label}
                            </ToggleGroupItem>
                          ))}
                        </ToggleGroup>
                      </div>
                    ) : null}

                    {checked && item.children ? (
                      <div className="flex flex-col gap-1 px-3.5 pb-3 pl-10.25">
                        {item.children.map((child) => {
                          const childChecked = state.selected.has(child.id)
                          const showRecommended = child.recommended && !childChecked
                          return (
                            <button
                              key={child.id}
                              type="button"
                              onClick={() => toggleSource(child.id as SourceId)}
                              className={cn(
                                'flex w-full gap-2.5 rounded-md border-0 px-2.5 py-2 text-left',
                                childChecked ? 'bg-brand-soft/40' : 'bg-bg-sunk'
                              )}
                            >
                              <Checkbox
                                checked={childChecked}
                                className="mt-0.5 pointer-events-none"
                                tabIndex={-1}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block text-13 font-medium text-ink">
                                  {child.label}
                                </span>
                                <span className="mt-0.5 block text-12 leading-snug text-ink-3">
                                  {child.desc}
                                </span>
                              </span>
                              {showRecommended ? (
                                <Badge
                                  variant="secondary"
                                  className="shrink-0 self-start border-transparent bg-st-warn-bg text-st-warn"
                                >
                                  Recommended
                                </Badge>
                              ) : null}
                            </button>
                          )
                        })}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </WizardChrome>
  )
}
